import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  TextField,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CampaignIcon from '@mui/icons-material/Campaign';

import { useActionSettings } from '../../hooks/useActionSettings';
import { useWebSocket } from '../WebSocketContext';
import ActionSettingsLayout from '../ActionSettingsHeader';
import TagsDropdown from '../TagsDropdown';
import TagEditor from '../Widgets/TagEditor';
import { buildGetExportSettingsPayload } from '../../Utils/actionSettingsUtils';

const ActionsLinkedinPost = ({
  caseid,
  tags,
  settingId,
  onSaveStatusChange,
  actionType = 'sendLinkedinPost',
  actionTitleKey = 'workflow.send_linkedin_post.title',
  actionTitleFallback = 'Publish post (LinkedIn)',
  i18nBaseKey = 'workflow.linkedin_post',
  isCompanyPost = false
}) => {
  const { t } = useTranslation();
  const { ws, subscribeToMessages, sendMessage } = useWebSocket();

  const [accountEmail, setAccountEmail] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [postBody, setPostBody] = useState('');
  const [visibility, setVisibility] = useState('PUBLIC');
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected | pending | connected
  const [connectionMessage, setConnectionMessage] = useState('');
  const [pendingDisconnect, setPendingDisconnect] = useState(false);
  const [verified, setVerified] = useState(false);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const [tagMenuAnchor, setTagMenuAnchor] = useState(null);
  const [tagTarget, setTagTarget] = useState(null);

  const bodyRef = useRef(null);

  const tBase = useCallback((suffix, fallback) => (
    t(`${i18nBaseKey}.${suffix}`, fallback)
  ), [t, i18nBaseKey]);

  const customComparisonCallback = useCallback((initial = {}) => {
    const initialAuth = initial.linkedinAuth || {};
    const template = initial.template || {};
    const initialVisibility = isCompanyPost ? 'PUBLIC' : (initial.visibility || 'PUBLIC');
    const initialCompany = initial.companyUrn || initial.companyId || '';

    return (
      postBody === (template.body || '') &&
      visibility === initialVisibility &&
      accountEmail === (initialAuth.account || '') &&
      verified === !!initialAuth.verified &&
      (!isCompanyPost || companyId === initialCompany) &&
      pendingDisconnect === false
    );
  }, [postBody, visibility, accountEmail, verified, companyId, pendingDisconnect, isCompanyPost]);

  const customSaveDataCallback = useCallback(() => {
    const linkedinAuth = {
      account: accountEmail,
      verified,
      ...(pendingDisconnect ? { disconnect: true } : {})
    };

    const payload = {
      template: {
        body: postBody
      },
      visibility: isCompanyPost ? 'PUBLIC' : visibility,
      linkedinAuth
    };

    if (isCompanyPost) {
      payload.companyId = companyId.trim();
    }

    return payload;
  }, [postBody, visibility, accountEmail, verified, pendingDisconnect, companyId, isCompanyPost]);

  const loadDataCallback = useCallback((response) => {
    if (!response?.customData) return;
    const data = response.customData;
    const template = data.template || {};
    const linkedinAuth = data.linkedinAuth || {};

    setPostBody(template.body || data.postBody || '');
    setVisibility(isCompanyPost ? 'PUBLIC' : (data.visibility || 'PUBLIC'));
    setCompanyId(data.companyUrn || data.companyId || '');
    setAccountEmail(linkedinAuth.account || linkedinAuth.preferredUsername || '');
    setVerified(!!linkedinAuth.verified);
    setPendingDisconnect(false);

    if (linkedinAuth.pendingState) {
      setConnectionStatus('pending');
      setConnectionMessage(tBase('awaiting_auth', 'Complete the LinkedIn login flow in the opened tab.'));
    } else if (linkedinAuth.verified) {
      setConnectionStatus('connected');
      setConnectionMessage(tBase('connected', 'Connected to LinkedIn account.'));
    } else {
      setConnectionStatus('disconnected');
      setConnectionMessage('');
    }
  }, [isCompanyPost, tBase]);

  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow,
    id
  } = useActionSettings({
    settingId,
    caseid,
    actionType,
    onSaveStatusChange,
    customComparison: customComparisonCallback,
    customSaveData: customSaveDataCallback,
    loadDataFunction: loadDataCallback
  });

  const isPersistedAction = useMemo(() => (
    id && !(typeof id === 'string' && id.startsWith('temp_'))
  ), [id]);

  const handleTagMenuOpen = (event, target) => {
    setTagTarget(target);
    setTagMenuAnchor(event.currentTarget);
  };

  const handleTagSelect = (tagValue) => {
    if (tagTarget === 'body' && bodyRef.current) {
      bodyRef.current.insertTag(tagValue);
    }

    setTagMenuAnchor(null);
    setTagTarget(null);
  };

  const handleConnect = () => {
    if (!ws) return;
    if (!isPersistedAction) {
      setConnectionMessage(tBase('save_before_connect', 'Save the action before connecting LinkedIn.'));
      return;
    }

    if (!accountEmail) {
      setConnectionMessage(tBase('enter_account', 'Enter your LinkedIn account email before connecting.'));
      return;
    }

    setIsAuthorizing(true);
    setConnectionMessage('');
    setPendingDisconnect(false);
    let returnUrl = window.location.href;
    try {
      const url = new URL(returnUrl);
      url.searchParams.set('openActions', 'true');
      url.searchParams.set('exportId', String(id));
      returnUrl = url.toString();
    } catch (err) {
      // keep original returnUrl
    }
    ws.send(JSON.stringify({
      command: 'authorizeLinkedinAction',
      caseid,
      exportId: id,
      account: accountEmail,
      returnUrl
    }));
  };

  const handleDisconnect = () => {
    setConnectionStatus('disconnected');
    setConnectionMessage(tBase('disconnect_pending', 'The connection will be removed after you save these settings.'));
    setPendingDisconnect(true);
    setVerified(false);
  };

  useEffect(() => {
    const unsubscribe = subscribeToMessages((message) => {
      if (!message) return;

      if (message.command === 'authorizeLinkedinActionStart') {
        const payload = message.data || {};
        if (payload.error) {
          setConnectionMessage(payload.error);
          setIsAuthorizing(false);
          return;
        }
        if (payload.exportId === id) {
          if (payload.url) {
            window.location.assign(payload.url);
          }
          setConnectionStatus('pending');
          setConnectionMessage(tBase('awaiting_auth', 'Complete the LinkedIn login flow in the opened tab.'));
          setIsAuthorizing(false);
        }
      } else if (message.command === 'linkedinActionTokenResult' && (message.data?.exportId === id)) {
        const payload = message.data || {};
        if (payload.verified) {
          setConnectionStatus('connected');
          setConnectionMessage(tBase('connected', 'Connected to LinkedIn account.'));
          setVerified(true);
          setPendingDisconnect(false);
          if (isPersistedAction) {
            sendMessage(buildGetExportSettingsPayload(id, caseid));
          }
        } else if (payload.error) {
          setConnectionStatus('disconnected');
          setConnectionMessage(payload.error);
        }
      } else if (message.command === 'verifyLinkedinActionTokenResult' && (message.data?.exportId === id)) {
        const payload = message.data || {};
        setIsCheckingConnection(false);
        if (payload.verified) {
          setConnectionStatus('connected');
          setConnectionMessage(tBase('connected', 'Connected to LinkedIn account.'));
          setVerified(true);
          setPendingDisconnect(false);
        } else {
          setConnectionStatus('disconnected');
          setConnectionMessage(payload.error || tBase('invalid_connection', 'LinkedIn connection requires re-authentication.'));
          setVerified(false);
        }
      }
    });

    return unsubscribe;
  }, [subscribeToMessages, id, tBase, ws, isPersistedAction, sendMessage]);

  useEffect(() => {
    if (!ws || !isPersistedAction) {
      return;
    }
    setIsCheckingConnection(true);
    setConnectionStatus('pending');
    setConnectionMessage(tBase('checking_status', 'Checking LinkedIn connection...'));
    ws.send(JSON.stringify({
      command: 'verifyLinkedinActionToken',
      caseid,
      exportId: id
    }));
  }, [ws, caseid, id, isPersistedAction, tBase]);

  const connectionChipColor = connectionStatus === 'connected'
    ? 'success'
    : connectionStatus === 'pending' || isCheckingConnection
      ? 'warning'
      : 'default';

  const connectionAlertSeverity = connectionStatus === 'connected'
    ? 'success'
    : connectionStatus === 'disconnected'
      ? 'error'
      : 'info';

  return (
    <>
      <ActionSettingsLayout
        isEnabled={isEnabled}
        onEnabledChange={setIsEnabled}
        title={t(actionTitleKey, actionTitleFallback)}
        isSaveDisabled={isSaveDisabled}
        onSave={handleSave}
        onForceRun={handleForceRun}
        forceRunLoading={forceRunLoading}
        conditions={conditions}
        onConditionsChange={handleConditionsChange}
        tags={tags}
        popUpOpen={popUpOpen}
        forceRunResult={forceRunResult}
        onCloseStatusWindow={handleCloseStatusWindow}
        forceRunWarningOpen={forceRunWarningOpen}
        forceRunWarningCount={forceRunWarningCount}
        onForceRunConfirm={handleForceRunConfirm}
        onForceRunCancel={handleForceRunCancel}
        primaryColor="#0A66C2"
      >
        <>
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" mb={2} gap={2}>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    {tBase('connection', 'LinkedIn connection')}
                  </Typography>
                  <Chip
                    size="small"
                    color={connectionChipColor}
                    label={connectionStatus === 'connected'
                      ? tBase('status_connected', 'Connected')
                      : connectionStatus === 'pending'
                        ? tBase('status_pending', 'Pending')
                        : tBase('status_disconnected', 'Disconnected')}
                  />
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={isCompanyPost ? 4 : 6}>
                    <TextField
                      label={tBase('account_email', 'LinkedIn account email')}
                      value={accountEmail}
                      onChange={(e) => setAccountEmail(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  {isCompanyPost && (
                    <Grid item xs={12} md={4}>
                      <TextField
                        label={tBase('company_id', 'LinkedIn company ID or URN')}
                        value={companyId}
                        onChange={(e) => setCompanyId(e.target.value)}
                        helperText={tBase('company_id_hint', 'Use a numeric ID or urn:li:organization:<id>.')}
                        fullWidth
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} md={isCompanyPost ? 4 : 6} display="flex" alignItems="center" gap={2}>
                    <Button
                      variant="contained"
                      startIcon={<LinkIcon />}
                      onClick={handleConnect}
                      disabled={isAuthorizing || isCheckingConnection || connectionStatus === 'connected'}
                    >
                      {tBase('connect', 'Connect LinkedIn')}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<LinkOffIcon />}
                      onClick={handleDisconnect}
                      disabled={connectionStatus === 'disconnected'}
                    >
                      {tBase('disconnect', 'Disconnect')}
                    </Button>
                  </Grid>
                  {connectionMessage && (
                    <Grid item xs={12}>
                      <Alert severity={connectionAlertSeverity}>
                        {connectionMessage}
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{tBase('visibility', 'Visibility')}</InputLabel>
              <Select
                label={tBase('visibility', 'Visibility')}
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
              >
                <MenuItem value="PUBLIC">{tBase('visibility_public', 'Public')}</MenuItem>
                {!isCompanyPost && (
                  <MenuItem value="CONNECTIONS">{tBase('visibility_connections', 'Connections only')}</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ mr: 2 }}>
                {tBase('body', 'Post content')}
              </Typography>
            </Box>
            <Box sx={{
              borderRadius: '12px',
              backgroundColor: '#fff',
              minHeight: 200,
              overflow: 'hidden',
              '&:hover': { borderColor: '#0A66C2' },
            }}>
              <TagEditor
                    caseid={caseid}
                ref={bodyRef}
                value={postBody}
                onChange={setPostBody}
                placeholder={tBase('body', 'Post content')}
                minRows={6}
                onTagClick={(e) => handleTagMenuOpen(e, 'body')}
                tags={tags}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Alert icon={<CampaignIcon />} severity="info">
              {tBase('tag_hint', 'Use tags such as {company_name} or {summary} to build automatic LinkedIn posts.')}
            </Alert>
          </Grid>
        </>
      </ActionSettingsLayout>

      <TagsDropdown
        tags={tags}
        handleTagSelect={handleTagSelect}
        anchorEl={tagMenuAnchor}
        setAnchorEl={setTagMenuAnchor}
      />
    </>
  );
};

export default ActionsLinkedinPost;

